import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext();

export const translations = {
  en: {
    // Navbar
    nav_home: 'Home',
    nav_programs: 'Programs',
    nav_insights: 'Insights',
    nav_gallery: 'Gallery',
    nav_about: 'About Us',
    nav_contact: 'Contact',
    nav_login: 'Login',
    nav_register: 'Register',
    nav_dashboard: 'My Dashboard',
    nav_logout: 'Logout',

    // Home hero
    hero_eyebrow: 'KPMG Africa Centre of Excellence',
    hero_title: 'Connecting Africa\'s Capital Ecosystem',
    hero_subtitle: 'A neutral, professionally curated platform bridging innovators, investors and ecosystem partners across the continent.',
    hero_cta_primary: 'Get Started',
    hero_cta_secondary: 'Learn More',

    // Vision pillars
    pillar_connect: 'Connect',
    pillar_connect_desc: 'Matchmaking between capital seekers and capital providers across sectors, stages and geographies.',
    pillar_strengthen: 'Strengthen',
    pillar_strengthen_desc: 'Investment-readiness support and structured profiling, aligned with institutional investor expectations.',
    pillar_inform: 'Inform',
    pillar_inform_desc: 'Curated insights, sector intelligence and thought leadership to support decision-making.',
    pillar_coordinate: 'Coordinate',
    pillar_coordinate_desc: 'A neutral space where regulators, development partners and the private sector engage with innovators.',

    // Who We Serve
    serve_title: 'Who We Serve',
    serve_subtitle: 'Designed around the actors shaping Africa\'s growth story.',
    serve_intro: 'Whether you are raising capital, deploying it, or shaping the ecosystem that enables both — the Centre of Excellence is designed with you in mind.',
    serve_innovators: 'Innovators & Enterprises',
    serve_innovators_tagline: 'Startups, SMEs, family businesses and growing ventures.',
    serve_investors: 'Investors',
    serve_investors_tagline: 'VCs, DFIs, banks, corporates, family offices and angels.',
    serve_regulators: 'Regulators & Ecosystem Partners',
    serve_regulators_tagline: 'Development partners, donors, accelerators and policymakers.',

    // Programs
    programs_title: 'Programs',
    programs_subtitle: 'KPMG Programs for Africa\'s Innovators',

    // Insights
    insights_title: 'Insights',
    insights_subtitle: 'Thought leadership and sector intelligence from KPMG Africa.',

    // Gallery
    gallery_title: 'Event Gallery',
    gallery_subtitle: 'Highlights from our summits, roundtables and pitch competitions.',
    gallery_empty: 'Photos coming soon. Check back after our next event.',

    // Contact
    contact_title: 'KPMG Africa Private Enterprise - Contact Team',
    contact_email_btn: 'Send us an Email',

    // Common
    explore: 'Explore',
    learn_more: 'Learn More',
    view_all: 'View All',
    back: 'Back',
    search_placeholder: 'Search by title or provider...',
  },

  fr: {
    // Navbar
    nav_home: 'Accueil',
    nav_programs: 'Programmes',
    nav_insights: 'Analyses',
    nav_gallery: 'Galerie',
    nav_about: 'À propos',
    nav_contact: 'Contact',
    nav_login: 'Connexion',
    nav_register: 'S\'inscrire',
    nav_dashboard: 'Mon tableau de bord',
    nav_logout: 'Déconnexion',

    // Home hero
    hero_eyebrow: 'KPMG Africa Centre d\'Excellence',
    hero_title: 'Connecter l\'Écosystème de Capital Africain',
    hero_subtitle: 'Une plateforme neutre et professionnelle reliant innovateurs, investisseurs et partenaires de l\'écosystème à travers le continent.',
    hero_cta_primary: 'Commencer',
    hero_cta_secondary: 'En savoir plus',

    // Vision pillars
    pillar_connect: 'Connecter',
    pillar_connect_desc: 'Mise en relation entre chercheurs de capitaux et fournisseurs de capitaux dans tous les secteurs, stades et zones géographiques.',
    pillar_strengthen: 'Renforcer',
    pillar_strengthen_desc: 'Soutien à la préparation aux investissements et profilage structuré, alignés sur les attentes des investisseurs institutionnels.',
    pillar_inform: 'Informer',
    pillar_inform_desc: 'Analyses sélectionnées, intelligence sectorielle et leadership éclairé pour soutenir la prise de décision.',
    pillar_coordinate: 'Coordonner',
    pillar_coordinate_desc: 'Un espace neutre où les régulateurs, partenaires de développement et le secteur privé s\'engagent avec les innovateurs.',

    // Who We Serve
    serve_title: 'Qui Nous Servons',
    serve_subtitle: 'Conçu autour des acteurs qui façonnent la croissance africaine.',
    serve_intro: 'Que vous leviez des capitaux, les déployiez, ou façonniez l\'écosystème — le Centre d\'Excellence est conçu pour vous.',
    serve_innovators: 'Innovateurs & Entreprises',
    serve_innovators_tagline: 'Startups, PME, entreprises familiales et entreprises en croissance.',
    serve_investors: 'Investisseurs',
    serve_investors_tagline: 'VCs, IFD, banques, corporates, family offices et angels.',
    serve_regulators: 'Régulateurs & Partenaires de l\'Écosystème',
    serve_regulators_tagline: 'Partenaires de développement, donateurs, accélérateurs et décideurs politiques.',

    // Programs
    programs_title: 'Programmes',
    programs_subtitle: 'Programmes KPMG pour les Innovateurs Africains',

    // Insights
    insights_title: 'Analyses',
    insights_subtitle: 'Leadership éclairé et intelligence sectorielle de KPMG Africa.',

    // Gallery
    gallery_title: 'Galerie d\'Événements',
    gallery_subtitle: 'Faits saillants de nos sommets, tables rondes et compétitions de pitch.',
    gallery_empty: 'Photos à venir. Revenez après notre prochain événement.',

    // Contact
    contact_title: 'KPMG Africa Private Enterprise - Équipe de Contact',
    contact_email_btn: 'Envoyez-nous un e-mail',

    // Common
    explore: 'Explorer',
    learn_more: 'En savoir plus',
    view_all: 'Voir tout',
    back: 'Retour',
    search_placeholder: 'Rechercher par titre ou fournisseur...',
  },
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('coe_language') || 'en';
  });

  const toggleLanguage = () => {
    const next = language === 'en' ? 'fr' : 'en';
    setLanguage(next);
    localStorage.setItem('coe_language', next);
  };

  const t = (key) => translations[language][key] || translations['en'][key] || key;

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
